import React, { useEffect, useRef, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { App, Button, Card, Col, Row, Spin, Typography } from "antd";
import { ArrowLeftOutlined, PrinterOutlined } from "@ant-design/icons";
import { useReactToPrint } from "react-to-print";
import { useSelector } from "react-redux";                          // ✅ added

import ReportDom from "../../../../components/common/ReportDom";
import { getInstitutionApi } from "../../../../apiservice/setting";
import { salesRecordWithDetailsApi } from "../../../../apiservice/sales";
      // ✅ added

import SalesVoucherBody from "../../../SalesVoucher/components/SalesVoucherBody";
import SalesInsuranceVoucher from "../../../SalesVoucher/components/SalesInsuranceVoucher";
import CorporateVoucher from "../../../SalesVoucher/components/CorporateVoucher";
import SalesThermalVoucher from "../../../SalesVoucher/components/SalesVoucherThermal";
import { selectAuthInfo } from "../../../Auth/authSelectors";
import { useShareDocument } from "../../../../hooks/useShareDocument";
import ShareDocumentButton from "../../../../components/ShareButton/ShareDocumentButton";

const { Title } = Typography;

const THERMAL_PRINT_STYLE = `
  @page {
    size: 112mm auto;
    margin: 0;
  }
  body {
    width: 112mm;
    margin: 0;
    padding: 0;
  }
`;

const SalesQuickVoucher = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { message } = App.useApp();

  const reportRef = useRef(null);
  const thermalRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [institution, setInstitution] = useState(null);
  const [voucher, setVoucher] = useState(null);
  const [customerEmail, setCustomerEmail] = useState("");
const thermalEnabled = institution?.thermal_print === 'yes';
  // ✅ Read Redux here — safe because this component is inside <Provider>
  // Pass authInfo as a plain prop to SalesThermalVoucher so it works
  // both in live render AND inside ReactDOMServer.renderToStaticMarkup
  const authInfo = useSelector(selectAuthInfo);

  const {
    shareOpen,
    setShareOpen,
    email,
    setEmail,
    handleEmailShare,
    handleWhatsAppShare,
  } = useShareDocument();

  const isPharmacyDrugStore =
    institution?.company_type === "Drug Store" ||
    institution?.company_type === "Pharmacy";

  const BodyComponent = useMemo(() => {
    if (!voucher) return null;
    if (voucher?.insurance_id) return "insurance";
    const isCorporate =
      (institution?.branch_company_type || "").toLowerCase() ===
      "information technology";
    if (isCorporate) return "corporate";
    return "normal";
  }, [voucher, institution]);

  const hasBonusItems = useMemo(
    () => voucher?.details?.some((item) => Number(item.item_rate) === 0),
    [voucher]
  );

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const [instRes, voucherRes] = await Promise.all([
          getInstitutionApi(),
          salesRecordWithDetailsApi({ sale_id: id, from: "voucher" }),
        ]);
        setInstitution(instRes?.data || null);
        if (Array.isArray(voucherRes?.data) && voucherRes.data.length > 0) {
        const voucherData = voucherRes.data[0];

        setVoucher(voucherData);
        setCustomerEmail(voucherData?.email_address || "");
        setEmail("");
      } else {
          setVoucher(null);
          message.warning("No sales voucher found.");
        }
      } catch (err) {
        console.error("Quick Sales Voucher load failed:", err);
        message.error("Failed to load sales voucher.");
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchAll();
  }, [id, message]);

  useEffect(() => {
    if (shareOpen && customerEmail ) {
      setEmail(customerEmail);
    }
  }, [shareOpen, customerEmail, email, setEmail]);

const handlePrint = useReactToPrint({
  contentRef: reportRef,
  documentTitle: `Sales_Voucher_${voucher?.sale_voucher_no || "Voucher"}`,
  removeAfterPrint: true,
  pageStyle: `
    @page { size: A4; margin: 10mm; }
  `,
});
  const handleThermalPrint = useReactToPrint({
    contentRef: thermalRef,
    documentTitle: `Thermal_${voucher?.sale_voucher_no || "Receipt"}`,
    pageStyle: THERMAL_PRINT_STYLE,
  });

  const renderLiveBody = () => {
    if (BodyComponent === "insurance")
      return (
        <SalesInsuranceVoucher
          sale_id={voucher.sale_id}
          institution={institution}
          printMode={false}
        />
      );
    if (BodyComponent === "corporate")
      return (
        <CorporateVoucher
          sale_id={voucher.sale_id}
          institution={institution}
          printMode={false}
        />
      );
    return (
      <>
        <SalesVoucherBody
          key="invoice-with-bonus"
          sale_id={voucher.sale_id}
          printMode={false}
          setCustomerEmail={setCustomerEmail}      
        />
        {isPharmacyDrugStore && hasBonusItems && (
          <>
            <div style={{ margin: "48px 0", borderTop: "1px dashed #ccc" }} />
            <SalesVoucherBody
              key="invoice-without-bonus"
              sale_id={voucher.sale_id}             
              printMode={false}
              hideBonusItems
              setCustomerEmail={setCustomerEmail}
            />
          </>
        )}
      </>
    );
  };

  const renderPrintBody = () => {
    if (BodyComponent === "insurance")
      return (
        <SalesInsuranceVoucher
          sale_id={voucher.sale_id}
          institution={institution}
          printMode
        />
      );
          if (BodyComponent === "corporate")
      return (
        <CorporateVoucher
          sale_id={voucher.sale_id}
          institution={institution}
          printMode
        />
      );
    return (
      <>
        <SalesVoucherBody key="print-with-bonus" sale_id={voucher.sale_id} printMode />
        {isPharmacyDrugStore && hasBonusItems && (
          <>
            <div style={{ pageBreakBefore: "always" }} />
            <SalesVoucherBody
              key="print-without-bonus"
              sale_id={voucher.sale_id}
              printMode
              hideBonusItems
            />
          </>
        )}
      </>
    );
  };

  // ✅ authInfo passed to every SalesThermalVoucher instance
  const renderThermalBody = () => (
    <>
      <SalesThermalVoucher
        voucher={voucher}
        institution={institution}
        authInfo={authInfo}
        printMode
        hideBonusItems={false}
      />
      {isPharmacyDrugStore && hasBonusItems && (
        <>
          <div
            style={{
              borderTop: "1px dashed #000",
              margin: "4px 0",
              pageBreakBefore: "always",
            }}
          />
          <SalesThermalVoucher
            voucher={voucher}
            institution={institution}
            authInfo={authInfo}
            printMode
            hideBonusItems
          />
        </>
      )}
    </>
  );

  // ✅ authInfo passed to preview instances too
  const renderThermalPreview = () => (
    <>
      <SalesThermalVoucher
        voucher={voucher}
        institution={institution}
        authInfo={authInfo}
        printMode={false}
        hideBonusItems={false}
      />
      {isPharmacyDrugStore && hasBonusItems && (
        <>
          <div style={{ margin: "32px 0", borderTop: "1px dashed #aaa" }} />
          <SalesThermalVoucher
            voucher={voucher}
            institution={institution}
            authInfo={authInfo}
            printMode={false}
            hideBonusItems
          />
        </>
      )}
    </>
  );

  if (loading)
    return (
      <div style={{ textAlign: "center", paddingTop: 100 }}>
        <Spin size="large" />
      </div>
    );

  if (!voucher)
    return (
      <div style={{ padding: 24 }}>
        <Title level={4}>No Voucher Found</Title>
        <Button onClick={() => navigate("/sales-dashboard")}>Back</Button>
      </div>
    );

  return (
    <div
      className="voucher-page-wrapper"
      style={{ padding: 24, background: "#f5f5f5", minHeight: "100vh" }}
    >

      {/* ── Header Bar ── */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }} gutter={[8, 8]}>
        <Col>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            Back
          </Button>
        </Col>
        <Col>
          <Row gutter={8}>
          <Col>
            <ShareDocumentButton
              disabled={!voucher}
              shareOpen={shareOpen}
              setShareOpen={setShareOpen}
              email={email}
              setEmail={setEmail}
              loading={loading}
              onEmail={() =>
                handleEmailShare({
                  url: `/print/sales/${voucher.sale_id}`,
                  fileName: `Invoice_${voucher.sale_voucher_no}.pdf`,
                  name: voucher.acc_name,
                  docNo: voucher.sale_voucher_no,
                })
              }
              onWhatsApp={() =>
                handleWhatsAppShare({
                  url: `/print/sales/${voucher.sale_id}`,
                  fileName: `Invoice_${voucher.sale_voucher_no}.pdf`,
                  text: `Hello ${voucher.acc_name}, Your Invoice ${voucher.sale_voucher_no} is ready. Download here:`,
                })
              }
            />
          </Col>

          <Col>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrint}
            >
              Print Voucher
            </Button>
          </Col>

          {thermalEnabled && (
            <Col>
              <Button
                icon={<PrinterOutlined />}
                onClick={handleThermalPrint}
                style={{
                  background: "#1a1a1a",
                  borderColor: "#1a1a1a",
                  color: "#fff",
                }}
              >
                Thermal Print (112mm)
              </Button>
            </Col>
          )}
        </Row>
        </Col>
      </Row>

      {/* ── A4 Voucher Body (screen) ── */}
      <Card
        className="shadow-md rounded-xl invoice-voucher-card"
        style={{ padding: 24, background: "white", marginBottom: 32 }}
      >
        {renderLiveBody()}
      </Card>

      {/* ── Thermal Preview (screen) ── */}
  {thermalEnabled && (
  <div style={{ display: "flex", justifyContent: "center", gap: 24, flexWrap: "wrap" }}>
    <div>
      <div style={{
        textAlign: "center", marginBottom: 8, fontWeight: 600,
        color: "#555", fontSize: 13, letterSpacing: "0.5px", textTransform: "uppercase",
      }}>
        Thermal Receipt Preview (112mm)
      </div>
      {renderThermalPreview()}
    </div>
  </div>
)}

      {/* ── Hidden: A4 printable DOM ── */}
  <div style={{ visibility: "hidden", height: 0, overflow: "hidden" }}>
  <ReportDom ref={reportRef} renderTable={renderPrintBody} />
</div>

      {/* ── Hidden: Thermal printable DOM ── */}
{/* ── Hidden: Thermal printable DOM ── */}
{thermalEnabled && (
  <div style={{ position: "absolute", left: "-9999px", top: 0 }}>
    <div  className="thermal-print-only"  ref={thermalRef}>
      {renderThermalBody()}
    </div>
  </div>
)}

    </div>
  );
};

export default SalesQuickVoucher;